import React, { useEffect, useState } from "react";
import { uniqueId } from "lodash";
import pubsub from "sweet-pubsub";
import { Container, NewStyledToast } from "./Styled";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import IconButton from "../../shared/components/IconButton";
import { Close } from "@mui/icons-material";
import { ErrorIcon } from "../../shared/svg/ErrorIcon";
import SuccessIcon from "../../shared/svg/SuccessIcon";
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import { Sentry } from "../../sentry";

interface ToastData {
    id: string;
    type: "success" | "error" | "warning" | "danger";
    title: string;
    message: string;
    duration: number;
}

export const Toast = () => {
    const [toasts, setToasts] = useState<ToastData[]>([]);

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((toast: ToastData) => toast.id !== id));
    };

    useEffect(() => {
        const addToast = ({
            type = "success",
            title,
            message,
            duration = 2,
        }: ToastData) => {
            const id = uniqueId("toast-");
            const normalizedType: ToastData["type"] =
                type === "danger" ? "error" : type;

            setToasts((prev) => [
                ...prev,
                { id, type: normalizedType, title, message, duration },
            ]);

            // Centralized: mirror error toasts to Sentry (no per-callsite changes).
            if (normalizedType === "error") {
                Sentry.withScope((scope) => {
                    scope.setTag("ui.event", "toast");
                    scope.setTag("ui.toast.type", String(type));
                    scope.setContext("toast", {
                        title,
                        message,
                        duration,
                    });
                    Sentry.captureMessage(
                        `UI toast error: ${title || "unknown"}`,
                        "error"
                    );
                });
            }

            if (duration) {
                setTimeout(() => removeToast(id), duration * 1000);
            }
        };

        pubsub.on("toast", addToast);

        return () => {
            pubsub.off("toast", addToast);
        };
    }, []);

    return (
        <Container data-testid="toast">
            <TransitionGroup>
                {toasts.map((toast) => (
                    <CSSTransition
                        key={toast.id}
                        classNames="app-toast"
                        timeout={200}
                    >
                        <NewStyledToast key={toast.id} type={toast.type} duration={toast.duration}>
                            <div className="content">
                                {toast.title && (
                                    <div className="title">
                                        {toast.type === "success" ? (
                                            <SuccessIcon />
                                        ) : toast.type === "warning" ? (
                                            <WarningOutlinedIcon color="warning" />
                                        ) : (
                                            <ErrorIcon />
                                        )}
                                        <span>{toast.title}</span>
                                    </div>
                                )}
                                {toast.message && (
                                    <div className="message">
                                        {toast.message}
                                    </div>
                                )}
                            </div>
                            <div className="close-icon-container">
                                <IconButton
                                    style={{ width: "24px" }}
                                    onClick={() => removeToast(toast.id)}
                                >
                                    <Close
                                        sx={{
                                            width: "16px",
                                            height: "16px",
                                        }}
                                    />
                                </IconButton>
                            </div>
                        </NewStyledToast>
                    </CSSTransition>
                ))}
            </TransitionGroup>
        </Container>
    );
};
